import * as React from 'react';
import { getCountryCallingCode } from 'libphonenumber-js';
import type { PhoneNumber } from '@hetivo/hetivo-lib-types';
import { SUPPORTED_COUNTRIES } from '@hetivo/hetivo-lib-types';

import { cn } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export interface PhoneInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'type' | 'value'> {
  onChange?: (value: PhoneNumber | null) => void;
  value?: PhoneNumber | null;
}

/** Converts an ISO 3166-1 alpha-2 country code (e.g. 'FR') to its flag emoji (e.g. '🇫🇷'). */
function countryCodeToFlag(code: string): string {
  return [...code.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');
}

/**
 * Resolves the dial code (calling code) for a given ISO 3166-1 alpha-2 country code.
 * Returns an empty string if the country code is invalid.
 */
function resolveDialCode(country: string): string {
  try {
    return `+${getCountryCallingCode(country as Parameters<typeof getCountryCallingCode>[0])}`;
  } catch {
    return '';
  }
}

/**
 * Performs a reverse lookup from a dial code to a supported country code.
 * Falls back to the first supported country when no match is found.
 */
function countryFromDialCode(dialCode: string): string {
  for (const country of SUPPORTED_COUNTRIES) {
    if (resolveDialCode(country) === dialCode) return country;
  }
  return SUPPORTED_COUNTRIES[0];
}

/**
 * A controlled phone input component that displays a selectable dial code dropdown
 * and an editable national number input.
 *
 * - Accepts and emits `PhoneNumber` objects (`{ dialCode, number }`)
 * - The displayed country is derived from `value.dialCode` — no `defaultCountry` prop needed
 * - User types only the national number portion
 * - Emits `null` when the national number is cleared
 * - Country flag emoji + dial code (e.g., 🇫🇷 +33, 🇱🇧 +961)
 * - Supports RTL layout via CSS logical properties
 * - Forwards `ref` to the inner `<input>` so FormControl (Radix Slot) can associate
 *   `id`, `aria-describedby`, `aria-invalid`, and `ref` with the actual input element
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, disabled, onChange, value, ...inputProps }, ref) => {
    const phoneCountry = countryFromDialCode(value?.dialCode ?? '');
    const dialCode = resolveDialCode(phoneCountry);
    const nationalNumber = value?.number ?? '';

    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      const digits = event.target.value;

      if (!digits) {
        onChange?.(null);
        return;
      }

      const cleaned = digits.replace(/\s/g, '');
      onChange?.({ dialCode, number: cleaned });
    }

    return (
      <div
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background text-base ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 md:text-sm',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        {/* Selectable dial code prefix */}
        <Select
          disabled={disabled}
          onValueChange={(val): void => {
            const newDialCode = resolveDialCode(val);
            onChange?.({ dialCode: newDialCode, number: value?.number ?? '' });
          }}
          value={phoneCountry}
        >
          <SelectTrigger
            aria-label="Phone country code"
            className="h-full w-auto shrink-0 gap-1 rounded-none rounded-s-md border-0 border-e border-input bg-background px-3 text-sm font-medium cursor-pointer hover:bg-accent hover:text-accent-foreground focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70"
          >
            <SelectValue>
              {countryCodeToFlag(phoneCountry)} {dialCode}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_COUNTRIES.map((code) => {
              const codeDialCode = resolveDialCode(code);
              return (
                <SelectItem key={code} value={code}>
                  {countryCodeToFlag(code)} {code} ({codeDialCode})
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* National number input — ref and inputProps forwarded here for FormControl/Slot compatibility */}
        <input
          {...inputProps}
          ref={ref}
          className="flex-1 bg-transparent px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed"
          disabled={disabled}
          onChange={handleChange}
          type="tel"
          value={nationalNumber}
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';
